import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { TOUR_SETTINGS_ROUTE, VEHICLE_ROUTE } from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { SearchEditor } from '../../features/search/SearchEditor';
import { updatePage } from '../../store/app/appSlice';
import { useGetVehicleProfileListQuery } from '../../store/facade/facadeApiV3';
import { selectItineraries } from '../../store/tour/tourSelectors';
import { SwitchPlanningMode } from '../SwitchPlanningMode/SwitchPlanningMode';
import { TourAlternatives } from '../TourAlternatives/TourAlternatives';
import { VehicleTypeSwitch } from '../VehicleOptions/VehicleTypeSwitch';
import { VehicleSelection } from '../VehicleSelection/VehicleSelection';
import { VehicleStatusIndicator } from '../../components/VehicleStatusIndicator/VehicleStatusIndicator';

export const commonSectionClassName = classNames('text-size-18 text-medium');
export const commonExpanderClassName = classNames('');
export const commonHeaderClassName = classNames('display-flex flex-column gap-10');
export const commonBodyClassName = classNames('rounded-bottom bg-lightest');

interface OptionsButtonProps {
    navigationPath: string;
}

const OptionsButton = ({ navigationPath }: OptionsButtonProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const handleBtnClick = () => {
        navigate(navigationPath, { replace: false });
        dispatch(updatePage(navigationPath));
    };

    return (
        <button
            data-testid='stopovers-options-btn'
            type='button'
            className='btn btn-link btn-sm padding-5 margin-left-10 text-color-gray'
            onClick={handleBtnClick}
        >
            <span className='margin-0' data-testid='srp-opt-icon'>
                <FormattedMessage id='intl-msg:smartRoute.generic.options' />
            </span>
        </button>
    );
};

export const SettingsOptions = () => {
    const itineraries = useAppSelector(selectItineraries);

    useGetVehicleProfileListQuery();

    return (
        <div className='SettingsOptions margin-bottom-15' data-testid='settings-options'>
            <div data-testid='vehicle-settings'>
                <div className={commonSectionClassName}>
                    <div className='display-flex align-items-center justify-content-between'>
                        <div className='display-flex align-items-center justify-content-start'>
                            <FormattedMessage id='intl-msg:smartRoute.generic.vehicle' />
                        </div>
                        <div className='display-flex'>
                            <OptionsButton navigationPath={VEHICLE_ROUTE} />
                        </div>
                    </div>
                </div>

                <hr className='margin-top-5' />

                <div className='text-color-dark margin-y-10 display-flex justify-content-between'>
                    <span>
                        <FormattedMessage id='intl-msg:smartRoute.generic.vehicleSelection' />
                        <VehicleStatusIndicator />
                        <SimpleTooltip
                            content={<FormattedMessage id='intl-msg:smartRoute.vehicle.noVehicleSelected.tooltip' />}
                            placement='bottom'
                        >
                            <span
                                className='rioglyph rioglyph-question-sign margin-left-5
                                    text-size-12'
                            />
                        </SimpleTooltip>
                    </span>
                    <VehicleTypeSwitch />
                </div>
                <VehicleSelection />
            </div>

            <div data-testid='route-settings' className='margin-top-50'>
                <div className={commonSectionClassName}>
                    <div className='display-flex align-items-center justify-content-between'>
                        <FormattedMessage id='intl-msg:smartRoute.tour.stopovers.title' />
                        <OptionsButton navigationPath={TOUR_SETTINGS_ROUTE} />
                    </div>
                </div>

                <hr className='margin-top-5' />

                <SwitchPlanningMode />

                <div data-testid='search-editor'>
                    <SearchEditor />
                </div>
            </div>

            {itineraries && (
                <div className='margin-top-25'>
                    <div className={commonSectionClassName}>
                        <FormattedMessage id='intl-msg:smartRoute.tour.alternatives.title' />
                    </div>

                    <hr className='margin-top-5' />

                    <TourAlternatives itineraries={itineraries} />
                </div>
            )}
        </div>
    );
};
